import { useMutation, useQuery } from '@apollo/client'
import {
    AdminPanelSettings,
    CheckCircle,
    Edit,
    RemoveCircle,
} from '@mui/icons-material'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    Menu,
    MenuItem,
    Stack,
} from '@mui/material'
import { DataGridPro, type GridColDef } from '@mui/x-data-grid-pro'
import { type AdminGetRecruitersQuery } from '__generated__/graphql'
import React from 'react'
import {
    RecruiterMe,
    SetRecruiterActive,
    SetRecruiterAdmin,
} from 'recruiter/graphql/admin.gql'
import useToast from 'shared/components/Toast/useToast'
import theme from 'shared/style/theme'
import { formatGraphQLError, getPxAsRem } from 'shared/utils'
import { type RecruiterToEdit } from './RecruiterModal'

interface RecruitersTableProps {
    data: AdminGetRecruitersQuery | undefined
    searchInput: string
    showInactive: boolean
    refetch: () => void
    onEdit: (recruiter: RecruiterToEdit) => void
}

interface RecruiterTableRow {
    id: string
    recruiterId: string
    name: string
    firstName: string
    lastName: string
    email: string
    lastActive: string | null
    isAdmin: boolean
    isActive: boolean
}

interface ConfirmAction {
    recruiterId: string
    name: string
    type: 'active' | 'admin'
    currentValue: boolean
}

export default function RecruitersTable({
    data,
    searchInput,
    showInactive,
    refetch,
    onEdit,
}: RecruitersTableProps) {
    const toast = useToast()
    const { data: meData } = useQuery(RecruiterMe)
    const currentRecruiterId = meData?.recruiterMe?.id
    const [setActiveMutation] = useMutation(SetRecruiterActive)
    const [setAdminMutation] = useMutation(SetRecruiterAdmin)
    const [confirmAction, setConfirmAction] =
        React.useState<ConfirmAction | null>(null)

    const recruiters = React.useMemo(() => {
        const all = data?.adminGetRecruiters ?? []
        return all.filter((r) => {
            if (!showInactive && !r.user?.isActive) return false
            if (searchInput) {
                const name = r.user?.preferredName?.toLowerCase() ?? ''
                return name.includes(searchInput.toLowerCase())
            }
            return true
        })
    }, [data, searchInput, showInactive])

    const handleSetActive = (recruiterId: string, isActive: boolean) => {
        setActiveMutation({
            variables: { recruiterId, isActive },
            onCompleted: (data) => {
                const action = isActive ? 'activated' : 'deactivated'
                if (data.setRecruiterActive.success) {
                    toast({
                        title: 'Success',
                        severity: 'success',
                        message: `Recruiter ${action}`,
                    })
                    refetch()
                } else {
                    toast({
                        title: 'Error',
                        severity: 'error',
                        message:
                            data.setRecruiterActive.message ??
                            `Failed to ${isActive ? 'activate' : 'deactivate'} recruiter`,
                    })
                }
            },
            onError: (error) => {
                toast({
                    title: 'Error',
                    severity: 'error',
                    message: formatGraphQLError(
                        error,
                        `Failed to ${isActive ? 'activate' : 'deactivate'} recruiter`
                    ),
                })
            },
        })
    }

    const handleSetAdmin = (recruiterId: string, isAdmin: boolean) => {
        setAdminMutation({
            variables: { recruiterId, isAdmin },
            onCompleted: (data) => {
                if (data.setRecruiterAdmin.success) {
                    toast({
                        title: 'Success',
                        severity: 'success',
                        message: `Admin status ${isAdmin ? 'granted' : 'revoked'}`,
                    })
                    refetch()
                } else {
                    toast({
                        title: 'Error',
                        severity: 'error',
                        message:
                            data.setRecruiterAdmin.message ??
                            'Failed to update admin status',
                    })
                }
            },
            onError: (error) => {
                toast({
                    title: 'Error',
                    severity: 'error',
                    message: formatGraphQLError(
                        error,
                        'Failed to update admin status'
                    ),
                })
            },
        })
    }

    const handleConfirm = () => {
        if (!confirmAction) return
        if (confirmAction.type === 'active') {
            handleSetActive(
                confirmAction.recruiterId,
                !confirmAction.currentValue
            )
        } else {
            handleSetAdmin(
                confirmAction.recruiterId,
                !confirmAction.currentValue
            )
        }
        setConfirmAction(null)
    }

    const columns: GridColDef<RecruiterTableRow>[] = [
        {
            field: 'name',
            headerName: 'Name',
            flex: 1,
            sortable: false,
            minWidth: parseInt(getPxAsRem(200), 10),
            renderCell: (params) => (
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <span>{params.row.name}</span>
                    {params.row.isAdmin && (
                        <Chip size="small" label="Admin" color="primary" />
                    )}
                    {!params.row.isActive && (
                        <Chip size="small" label="Inactive" color="default" />
                    )}
                </Stack>
            ),
        },
        {
            field: 'email',
            headerName: 'Email',
            flex: 1,
            sortable: false,
            minWidth: parseInt(getPxAsRem(200), 10),
        },
        {
            field: 'lastActive',
            headerName: 'Last Active',
            sortable: false,
            minWidth: parseInt(getPxAsRem(150), 10),
            renderCell: (params) => <span>{params.row.lastActive ?? '-'}</span>,
        },
        {
            field: 'actions',
            headerName: 'Actions',
            sortable: false,
            minWidth: parseInt(getPxAsRem(100), 10),
            renderCell: (params) => (
                <RecruiterActions
                    isActive={params.row.isActive}
                    isAdmin={params.row.isAdmin}
                    isSelf={params.row.recruiterId === currentRecruiterId}
                    onEdit={() =>
                        onEdit({
                            recruiterId: params.row.recruiterId,
                            email: params.row.email,
                            firstName: params.row.firstName,
                            lastName: params.row.lastName,
                        })
                    }
                    onToggleActive={() =>
                        setConfirmAction({
                            recruiterId: params.row.recruiterId,
                            name: params.row.name,
                            type: 'active',
                            currentValue: params.row.isActive,
                        })
                    }
                    onToggleAdmin={() =>
                        setConfirmAction({
                            recruiterId: params.row.recruiterId,
                            name: params.row.name,
                            type: 'admin',
                            currentValue: params.row.isAdmin,
                        })
                    }
                />
            ),
        },
    ]

    const rows: RecruiterTableRow[] = recruiters.map((r) => ({
        id: r.id,
        recruiterId: r.id,
        name: r.user?.preferredName ?? '-',
        firstName: r.user?.firstName ?? '',
        lastName: r.user?.lastName ?? '',
        email: r.user?.email ?? '-',
        lastActive: r.lastLogin
            ? new Date(r.lastLogin).toLocaleDateString()
            : null,
        isAdmin: r.isAdmin,
        isActive: r.user?.isActive ?? false,
    }))

    return (
        <>
            <div style={{ width: '100%', height: '100%' }}>
                <DataGridPro
                    sx={{ border: 0 }}
                    density="compact"
                    disableColumnMenu
                    hideFooter
                    disableColumnReorder
                    disableColumnResize
                    showCellVerticalBorder={false}
                    showColumnVerticalBorder={false}
                    columns={columns}
                    rows={rows}
                />
            </div>
            <Dialog
                open={confirmAction !== null}
                onClose={() => setConfirmAction(null)}
            >
                <DialogTitle>
                    {confirmAction?.type === 'active'
                        ? confirmAction.currentValue
                            ? 'Deactivate'
                            : 'Reactivate'
                        : confirmAction?.currentValue
                          ? 'Remove Admin'
                          : 'Make Admin'}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to{' '}
                        {confirmAction?.type === 'active'
                            ? confirmAction.currentValue
                                ? 'deactivate'
                                : 'reactivate'
                            : confirmAction?.currentValue
                              ? 'remove admin status from'
                              : 'make'}{' '}
                        {confirmAction?.name}
                        {confirmAction?.type === 'admin' &&
                        !confirmAction.currentValue
                            ? ' an admin'
                            : ''}
                        ?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        variant="text"
                        onClick={() => setConfirmAction(null)}
                    >
                        Cancel
                    </Button>
                    <Button onClick={handleConfirm}>Confirm</Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

function RecruiterActions({
    isActive,
    isAdmin,
    isSelf,
    onEdit,
    onToggleActive,
    onToggleAdmin,
}: {
    isActive: boolean
    isAdmin: boolean
    isSelf: boolean
    onEdit: () => void
    onToggleActive: () => void
    onToggleAdmin: () => void
}) {
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
    const open = Boolean(anchorEl)

    return (
        <Box
            display="flex"
            justifyContent="flex-start"
            alignItems="center"
            height="100%"
        >
            <IconButton
                size="small"
                onClick={(e) => setAnchorEl(e.currentTarget)}
            >
                <MoreVertIcon fontSize="small" />
            </IconButton>
            {open && (
                <Menu
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    open={open}
                    onClose={() => setAnchorEl(null)}
                    anchorEl={anchorEl}
                    onClick={() => setAnchorEl(null)}
                >
                    <MenuItem onClick={onEdit}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Edit sx={{ color: theme.palette.grey[600] }} />
                            <Box>Edit</Box>
                        </Stack>
                    </MenuItem>
                    {!isSelf && (
                        <MenuItem onClick={onToggleAdmin}>
                            <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                            >
                                <AdminPanelSettings
                                    sx={{ color: theme.palette.grey[600] }}
                                />
                                <Box>
                                    {isAdmin ? 'Remove Admin' : 'Make Admin'}
                                </Box>
                            </Stack>
                        </MenuItem>
                    )}
                    <MenuItem onClick={onToggleActive}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            {isActive ? (
                                <>
                                    <RemoveCircle
                                        sx={{ color: theme.palette.grey[600] }}
                                    />
                                    <Box>Deactivate</Box>
                                </>
                            ) : (
                                <>
                                    <CheckCircle
                                        sx={{ color: theme.palette.grey[600] }}
                                    />
                                    <Box>Reactivate</Box>
                                </>
                            )}
                        </Stack>
                    </MenuItem>
                </Menu>
            )}
        </Box>
    )
}
